import { action, createAsyncAction } from 'typesafe-actions'
import { Dispatch } from 'redux'
import api from 'api/graphql/graphql'
import { AccountDetails, ErrorMessages, PersonIDEnum, ProfileSection } from 'types/global'
import { Player } from 'types/global'
import { ApolloError } from 'apollo-boost'
import i18n from '../i18n'
import { toast } from 'react-toastify'
import { MessageTypes } from 'constants/message-screens'
import { PersonAPIUpdateInput } from 'types/api/types'
import { updateAvatar, uploadAvatar } from 'utils'

export const UPDATE_OWN_PROFILE_REQUESTED = 'profile/UPDATE_OWN_PROFILE_REQUESTED'
export const UPDATE_OWN_PROFILE_COMPLETED = 'profile/UPDATE_OWN_PROFILE_COMPLETED'
export const UPDATE_OWN_PROFILE_FAILED = 'profile/UPDATE_OWN_PROFILE_FAILED'
export const requestUpdateOwnProfile = createAsyncAction(
  UPDATE_OWN_PROFILE_REQUESTED,
  UPDATE_OWN_PROFILE_COMPLETED,
  UPDATE_OWN_PROFILE_FAILED
)<void, Player, Error>()

export const PLAYER_PROFILE_REQUESTED = 'profile/PLAYER_PROFILE_REQUESTED'
export const PLAYER_PROFILE_COMPLETED = 'profile/PLAYER_PROFILE_COMPLETED'
export const PLAYER_PROFILE_FAILED = 'profile/PLAYER_PROFILE_FAILED'
export const requestPlayerProfile = createAsyncAction(
  PLAYER_PROFILE_REQUESTED,
  PLAYER_PROFILE_COMPLETED,
  PLAYER_PROFILE_FAILED
)<void, Player, { error: Error; type: ErrorMessages }>()

export const OWN_PROFILE_REQUESTED = 'profile/OWN_PROFILE_REQUESTED'
export const OWN_PROFILE_COMPLETED = 'profile/OWN_PROFILE_COMPLETED'
export const OWN_PROFILE_FAILED = 'profile/OWN_PROFILE_FAILED'
export const requestOwnProfile = createAsyncAction(
  OWN_PROFILE_REQUESTED,
  OWN_PROFILE_COMPLETED,
  OWN_PROFILE_FAILED
)<void, Player, { error: Error; type: ErrorMessages }>()

export const MATCH_RESULTS_REQUESTED = 'match-results/MATCH_RESULTS_REQUESTED'
export const MATCH_RESULTS_COMPLETED = 'match-results/MATCH_RESULTS_COMPLETED'
export const MATCH_RESULTS_FAILED = 'match-results/MATCH_RESULTS_FAILED'
export const requestMatchResults = createAsyncAction(
  MATCH_RESULTS_REQUESTED,
  MATCH_RESULTS_COMPLETED,
  MATCH_RESULTS_FAILED
)<void, any, Error>()

export const LOAD_MORE_MATCH_RESULTS_REQUESTED = 'match-results/LOAD_MORE_MATCH_RESULTS_REQUESTED'
export const LOAD_MORE_MATCH_RESULTS_COMPLETED = 'match-results/LOAD_MORE_MATCH_RESULTS_COMPLETED'
export const LOAD_MORE_MATCH_RESULTS_FAILED = 'match-results/LOAD_MORE_MATCH_RESULTS_FAILED'
export const loadMoreMatchResults = createAsyncAction(
  LOAD_MORE_MATCH_RESULTS_REQUESTED,
  LOAD_MORE_MATCH_RESULTS_COMPLETED,
  LOAD_MORE_MATCH_RESULTS_FAILED
)<void, any, Error>()

export const SET_FIRST_TOURNAMENT_DATE = 'profile/SET_FIRST_TOURNAMENT_DATE'

export const PLAYER_STATS_REQUESTED = 'player-stats/PLAYER_STATS_REQUESTED'
export const PLAYER_STATS_COMPLETED = 'player-stats/PLAYER_STATS_COMPLETED'
export const PLAYER_STATS_FAILED = 'player-stats/PLAYER_STATS_FAILED'
export const requestPlayerStats = createAsyncAction(
  PLAYER_STATS_REQUESTED,
  PLAYER_STATS_COMPLETED,
  PLAYER_STATS_FAILED
)<void, any, Error>()

export const UPDATE_PROFILE_AVATAR_REQUESTED = 'profile/UPDATE_PROFILE_AVATAR_REQUESTED'
export const UPDATE_PROFILE_AVATAR_COMPLETED = 'profile/UPDATE_PROFILE_AVATAR_COMPLETED'
export const UPDATE_PROFILE_AVATAR_FAILED = 'profile/UPDATE_PROFILE_AVATAR_FAILED'

export const requestUpdateProfileAvatar = createAsyncAction(
  UPDATE_PROFILE_AVATAR_REQUESTED,
  UPDATE_PROFILE_AVATAR_COMPLETED,
  UPDATE_PROFILE_AVATAR_FAILED
)<void, string, Error>()

export const SET_TOURNAMENTS_DATE_RANGE = 'match-results/SET_TOURNAMENTS_DATE_RANGE'
export const SET_ACTIVE_SECTION = 'profile/SET_ACTIVE_SECTION'
export const setActiveSection = (section: ProfileSection) => action(SET_ACTIVE_SECTION, section)
export const setFirstTournamentDate = date => action(SET_FIRST_TOURNAMENT_DATE, date)
export const setTournamentsDateRange = (dateRange: { start?: string; end?: string }) =>
  action(SET_TOURNAMENTS_DATE_RANGE, dateRange)

export const getOwnProfile = () => {
  return async (dispatch: Dispatch) => {
    dispatch(requestOwnProfile.request())
    try {
      dispatch(requestOwnProfile.success(await api.getOwnProfile()))
    } catch (e) {
      console.warn('Failed to fetch own profile', e && (e as Error)?.message)
      dispatch(requestOwnProfile.failure({ error: e as Error, type: MessageTypes.DEFAULT }))
    }
  }
}

export const updateOwnProfile =
  (profileInput: PersonAPIUpdateInput, hideToastMessages?: Boolean) =>
  async (dispatch: Dispatch) => {
    try {
      dispatch(requestUpdateOwnProfile.request())
      dispatch(
        requestUpdateOwnProfile.success(
          !hideToastMessages
            ? await toast.promise(api.updateOwnProfile(profileInput), {
                pending: i18n.t('updatingDetails', { ns: 'strAlerts' }),
                error: i18n.t('detailsNotUpdated', { ns: 'strAlerts' }),
                success: i18n.t('detailsUpdated', { ns: 'strAlerts' }),
              })
            : await api.updateOwnProfile(profileInput)
        )
      )
    } catch (e) {
      console.warn('Failed to update own profile', e && (e as Error)?.message)
      dispatch(requestUpdateOwnProfile.failure(e as Error))
    }
  }

export const loadProfile = ({
  id,
  type,
  isLoggedIn = false,
}: {
  id: string
  type: PersonIDEnum
  isLoggedIn?: boolean
}) => {
  if (type === PersonIDEnum.TennisID && typeof id === 'string') id = id.toUpperCase()

  return async dispatch => {
    try {
      dispatch(requestPlayerProfile.request())
      dispatch(
        requestPlayerProfile.success(
          await api.getProfile({ id, idType: type, includeProtectedFields: isLoggedIn })
        )
      )
    } catch (e) {
      let errorType = MessageTypes.NETWORK
      const { networkError } = e as ApolloError
      if (!networkError) {
        errorType = MessageTypes.PLAYER_NOT_FOUND
      }
      console.error('Failed to fetch player profile', e && (e as Error)?.message)
      dispatch(requestPlayerProfile.failure({ error: e as Error, type: errorType }))
    }
  }
}

export interface GetMatchResultListingsOptions {
  skip: number
  start: string
  end: string
  resetMatchResultListings?: boolean
}

export const getMatchResultListings = (
  playerId: string,
  options: GetMatchResultListingsOptions
) => {
  const { skip, start, end, resetMatchResultListings } = options
  return (dispatch: Dispatch) => {
    const action = resetMatchResultListings ? requestMatchResults : loadMoreMatchResults
    let queryOptions
    if (!start) {
      queryOptions = { skip }
    } else {
      queryOptions = {
        skip,
        start,
        end,
      }
    }

    dispatch(action.request())
    api
      .getPlayerMatchResultListings(playerId, queryOptions)
      .then(data => {
        const { firstTournamentDate, matchResultsListings } = data
        dispatch(setFirstTournamentDate(firstTournamentDate))
        dispatch(action.success(matchResultsListings))
      })
      .catch(e => {
        console.warn('Failed to fetch player match results', e && e.message)
        dispatch(action.failure(e))
      })
  }
}

export const getPlayerStats = (playerId: string) => (dispatch: Dispatch) => {
  dispatch(requestPlayerStats.request())
  api
    .getPlayerStats(playerId)
    .then(data => dispatch(requestPlayerStats.success(data)))
    .catch(e => {
      console.warn('Failed to fetch player stats', e && e.message)
      dispatch(requestPlayerStats.failure(e))
    })
}

export const uploadProfileAvatar = file => async dispatch => {
  dispatch(requestUpdateProfileAvatar.request())
  try {
    dispatch(
      requestUpdateProfileAvatar.success(
        await toast.promise(uploadAvatar(file), {
          pending: i18n.t('updatingDetails', { ns: 'strAlerts' }),
          error: i18n.t('detailsNotUpdated', { ns: 'strAlerts' }),
          success: i18n.t('detailsUpdated', { ns: 'strAlerts' }),
        })
      )
    )
  } catch (e) {
    console.warn('Failed to update avatar', e && (e as Error)?.message)
    dispatch(requestUpdateProfileAvatar.failure(e as Error))
  }
}

export const updateProfileAvatar = (avatarUrl: string) => async dispatch => {
  dispatch(requestUpdateProfileAvatar.request())
  try {
    dispatch(
      requestUpdateProfileAvatar.success(
        await toast.promise(updateAvatar(avatarUrl), {
          pending: i18n.t('updatingDetails', { ns: 'strAlerts' }),
          error: i18n.t('detailsNotUpdated', { ns: 'strAlerts' }),
          success: i18n.t('detailsUpdated', { ns: 'strAlerts' }),
        })
      )
    )
  } catch (e) {
    console.warn('Failed to update avatar', e && (e as Error)?.message)
    dispatch(requestUpdateProfileAvatar.failure(e as Error))
  }
}
